import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/UI/Button';
import { conversationService } from '../../services/conversationService';
import { inboxService } from '../../services/inboxService';
import { supportGroupService } from '../../services/supportGroupService';
import { labelService } from '../../services/labelService';
import { userService } from '../../services/userService';
import { Conversation } from '../../types/conversation';
import { Inbox } from '../../types/inbox';
import { SupportGroup } from '../../types/supportGroup';
import { Label } from '../../types/label';
import { User } from '../../types/users';
import {
  CHANNEL_TYPE_LABELS,
  CONVERSATION_STATUS_COLORS,
  CONVERSATION_STATUS_LABELS,
  ConversationStatus,
  ChannelType,
} from '../../types/atendimentoEnums';
import { useToast } from '../../components/UI/ToastProvider';
import { ChannelTypeIcon } from '../../components/Atendimento/ChannelTypeIcon';
import { useConversationHub } from '../../hooks/useConversationHub';
import { useAuth } from '../../contexts/AuthContext';
import { helpdeskRoutes } from '../../routes/helpdeskRoutes';

const INBOX_DEFAULT_STATUSES: ConversationStatus[] = [
  ConversationStatus.Open,
  ConversationStatus.Pending,
  ConversationStatus.WaitingForOperator,
  ConversationStatus.InProgress,
  ConversationStatus.Reopened,
];

const formatPhone = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, '');
  
  if (cleaned.length === 13)
  {
    return `+${cleaned.slice(0, 2)} (${cleaned.slice(2, 4)}) ${cleaned.slice(4, 9)}-${cleaned.slice(9)}`;
  }
  
  if (cleaned.length === 11)
  {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
  }
  
  if (cleaned.length === 10)
  {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
  }
  
  return phone;
};

const formatContactDetail = (conversation: Conversation): string => {
  const parts: string[] = [];
  const addedValues = new Set<string>();

  if (conversation.contactEmail)
  {
    parts.push(conversation.contactEmail);
    addedValues.add(conversation.contactEmail.toLowerCase());
  }

  const phoneNormalized = conversation.contactPhone?.replace(/\D/g, '');
  const identifierNormalized = conversation.contactIdentifier?.replace(/\D/g, '');

  if (conversation.contactPhone && !addedValues.has(phoneNormalized || ''))
  {
    parts.push(formatPhone(conversation.contactPhone));
    addedValues.add(phoneNormalized || '');
  }

  if (conversation.contactIdentifier && 
      !addedValues.has(identifierNormalized || '') &&
      identifierNormalized !== phoneNormalized)
  {
    parts.push(conversation.contactIdentifier);
  }

  return parts.length > 0 ? parts.join(' • ') : 'Sem informações de contato';
};

const formatRelativeTime = (date: string): string => {
  const now = new Date();
  const past = new Date(date);
  const diffMs = now.getTime() - past.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1)
  {
    return 'Agora mesmo';
  }
  
  if (diffMins < 60)
  {
    return `Há ${diffMins} min`;
  }
  
  if (diffHours < 24)
  {
    return `Há ${diffHours}h`;
  }
  
  if (diffDays === 1)
  {
    return 'Ontem';
  }
  
  if (diffDays < 7)
  {
    return `Há ${diffDays} dias`;
  }
  
  return past.toLocaleDateString('pt-BR', { 
    day: '2-digit', 
    month: 'short' 
  });
};

export const AtendimentoInboxPage: React.FC = () => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [inboxes, setInboxes] = useState<Inbox[]>([]);
  const [groups, setGroups] = useState<SupportGroup[]>([]);
  const [labels, setLabels] = useState<Label[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ConversationStatus | ''>('');
  const [channelFilter, setChannelFilter] = useState<ChannelType | ''>('');
  const [groupFilter, setGroupFilter] = useState('');
  const [labelFilter, setLabelFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 400);

    return () => {
      clearTimeout(timer);
    };
  }, [searchInput]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try
    {
      const [inboxesData, groupsData, labelsData, usersResult, conversationsData] = await Promise.all([
        inboxService.getAll(),
        supportGroupService.getAll(),
        labelService.getAll(),
        userService.getUsers('', 1, 200),
        conversationService.getAll({
          statuses: statusFilter !== '' ? [statusFilter] : INBOX_DEFAULT_STATUSES,
          channelType: channelFilter !== '' ? channelFilter : undefined,
          supportGroupId: groupFilter || undefined,
          labelId: labelFilter || undefined,
          assigneeId: assigneeFilter || undefined,
          search: debouncedSearch || undefined,
        }),
      ]);

      const myInboxIds = inboxesData
        .filter(inbox => user?.id && inbox.memberUserIds.includes(user.id))
        .map(inbox => inbox.id);

      const filtered = myInboxIds.length > 0
        ? conversationsData.filter(c => myInboxIds.includes(c.inboxId))
        : conversationsData;

      setInboxes(inboxesData);
      setGroups(groupsData);
      setLabels(labelsData.filter(l => l.isActive));
      setUsers(usersResult.users);
      setConversations(filtered);
    }
    catch
    {
      addToast('Erro ao carregar caixa de entrada', 'error');
    }
    finally
    {
      setLoading(false);
    }
  }, [
    addToast,
    user?.id,
    statusFilter,
    channelFilter,
    groupFilter,
    labelFilter,
    assigneeFilter,
    debouncedSearch,
  ]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useConversationHub({
    callbacks: {
      onConversationListChanged: () => {
        void loadData();
      },
      onConversationUpdated: () => {
        void loadData();
      },
      onConversationAssigned: () => {
        void loadData();
      },
    },
  });

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Minhas notificações</h1>
        <p className="text-gray-600 text-sm mt-1">
          Conversas das caixas em que você é membro
          {inboxes.length > 0 && ` (${inboxes.length} caixa${inboxes.length > 1 ? 's' : ''})`}
        </p>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6 flex flex-col gap-4">
        <input
          type="text"
          placeholder="Buscar por nome, telefone, e-mail, documento ou conteúdo de mensagens..."
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
        />
        <div className="flex flex-col md:flex-row flex-wrap gap-4">
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value === '' ? '' : e.target.value as ConversationStatus)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="">Em andamento (padrão)</option>
            {Object.entries(CONVERSATION_STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <select
            value={channelFilter}
            onChange={e => setChannelFilter(e.target.value === '' ? '' : e.target.value as ChannelType)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="">Todos os canais</option>
            {Object.entries(CHANNEL_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <select
            value={groupFilter}
            onChange={e => setGroupFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="">Todos os grupos</option>
            {groups.map(group => (
              <option key={group.id} value={group.id}>{group.name}</option>
            ))}
          </select>
          <select
            value={labelFilter}
            onChange={e => setLabelFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="">Todas as etiquetas</option>
            {labels.map(label => (
              <option key={label.id} value={label.id}>{label.name}</option>
            ))}
          </select>
          <select
            value={assigneeFilter}
            onChange={e => setAssigneeFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="">Todos os responsáveis</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
        </div>
      ) : conversations.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-500 mb-2">Nenhuma conversa pendente nas suas caixas.</p>
          <p className="text-gray-400 text-sm mb-6">Notificações de todas as caixas inscritas aparecerão aqui.</p>
          <Link to={helpdeskRoutes.workspace}>
            <Button variant="secondary">Abrir workspace</Button>
          </Link>
        </div>
      ) : (
        <div className="grid gap-4">
          {conversations.map(conversation => {
            const contactDetail = formatContactDetail(conversation);

            return (
              <div key={conversation.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-sm font-medium text-gray-500">#{conversation.displayId}</span>
                      <h3 className="text-lg font-semibold text-gray-900 truncate">
                        {conversation.contactName}
                      </h3>
                      <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full whitespace-nowrap ${CONVERSATION_STATUS_COLORS[conversation.status]}`}>
                        {CONVERSATION_STATUS_LABELS[conversation.status]}
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-3">
                      {contactDetail}
                    </p>
                    
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500">
                      <div className="flex items-center gap-1.5">
                        <ChannelTypeIcon channelType={conversation.channelType} />
                        <span>{CHANNEL_TYPE_LABELS[conversation.channelType] ?? conversation.channelType}</span>
                      </div>
                      
                      <div className="flex items-center gap-1.5">
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                        </svg>
                        <span>{conversation.inboxName}</span>
                      </div>
                      
                      {conversation.assigneeName && (
                        <div className="flex items-center gap-1.5">
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span>{conversation.assigneeName}</span>
                        </div>
                      )}
                      
                      <div className="flex items-center gap-1.5">
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{formatRelativeTime(conversation.lastActivityAt)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <Link to={helpdeskRoutes.conversation(conversation.id)}>
                    <Button variant="primary" size="sm">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </Button>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
